import { translate } from "app/language/service"
import { getStore } from "app/store/store"
import { IAudience, IAudienceSetFilterParams } from "audience/model"
import { audienceRPO } from "audience/repository"
import { getAllAudiencesInStore, getAudienceNameFromId } from "audience/tools/common"
import { refreshInventory } from "form/inventory/tools/common"
import { IForm, IFormChange, IFormDataLoader, SELECT_FILTER_VALUES } from "form/model"
import { state } from "form/state"
import { formActionCreator } from "form/store/actions"
import { getForm } from "form/tools/common"
import { messageSVC } from "message/service"
import { normalizeSync } from "normalize-diacritics"
import { getFinalizedArrayChange } from "./change"

export function getAudienceChanges(form: IForm): IFormChange[] {
  return [
    getFinalizedArrayChange(
      "audience.title",
      form.context.initialData ? form.context.initialData.audience.selectedIds : undefined,
      form.audience.selectedIds,
      (id: any) => getAudienceNameFromId(id)
    )
  ]
}

export const audienceFormDataLoader: IFormDataLoader = async (form: IForm, onFinish: (error?: string) => any) => {
  try {
    const audiences = await audienceRPO.getCampaignExternalSegment(form.campaignId)
    form.context.relations.audiences = audiences
    form.audience.selectedIds = audiences.map(a => a.external_segment_id)
    onFinish()
  } catch (error) {
    console.error(error)
    onFinish(`Audience form data-loader error ${String(error)}`)
  }
}

export function synchronizeNewAudienceSelection(newSelectedAudienceIds: number[]): void {
  getStore().dispatch(formActionCreator.startSynchronizingAudience(getForm().context.appContext))
  clearTimeout(state.environmentsTimeout)
  state.environmentsTimeout = setTimeout(() => setAudiences(newSelectedAudienceIds), 800)
}

export function setAudienceFilters(params: IAudienceSetFilterParams): void {
  getStore().dispatch(formActionCreator.setAudiencesFilter(getForm().context.appContext, params))
}

export function getFilteredAudiences(
  nameFilter: string,
  selectFilter: SELECT_FILTER_VALUES,
  selectedIds: number[]
): IAudience[] {
  const allAudiences = getAllAudiencesInStore()
  if (!nameFilter && selectFilter === SELECT_FILTER_VALUES.ALL) {
    return allAudiences
  }
  const filteredAudiences: IAudience[] = []
  const preparedNameFilter = normalizeSync(nameFilter).toLowerCase()
  for (const audience of allAudiences) {
    if (nameFilter && audience.normalizedName.indexOf(preparedNameFilter) === -1) {
      continue
    }
    if (selectFilter !== SELECT_FILTER_VALUES.ALL) {
      const isSelected = selectedIds.includes(audience.id)
      if (selectFilter === SELECT_FILTER_VALUES.SELECTED) {
        if (!isSelected) {
          continue
        }
      } else if (isSelected) {
        continue
      }
    }
    filteredAudiences.push(audience)
  }
  return filteredAudiences
}

export function noticeAndClearAudienceError(form: IForm): void {
  messageSVC.error(translate("audience.error.selection"))
  getStore().dispatch(formActionCreator.setRelations(form.context.appContext, { audiencesError: false }))
}

// @recursive
async function setAudiences(newSelectedAudienceIds: number[]): Promise<void> {
  if (state.audienceIsSynchronizing) {
    state.audienceToApplySelection = newSelectedAudienceIds
    return
  } else {
    state.audienceIsSynchronizing = true
  }
  const form = getForm()
  let hasError = false
  try {
    const newAudienceAssociations = await audienceRPO.setCampaignExternalSegments(
      form.tempCampaignId,
      newSelectedAudienceIds.map(id => ({ campaign_id: form.tempCampaignId, external_segment_id: id }))
    )
    getStore().dispatch(
      formActionCreator.setFormAudience(
        form.context.appContext,
        getAudienceChanges({
          ...form,
          audience: {
            ...form.audience,
            selectedIds: newSelectedAudienceIds
          }
        }),
        newAudienceAssociations
      )
    )
  } catch (error) {
    console.error(error)
    hasError = true
    getStore().dispatch(formActionCreator.setRelations(form.context.appContext, { audiencesError: true }))
    getStore().dispatch(formActionCreator.stopSynchronizingAudience(form.context.appContext))
  }
  state.audienceIsSynchronizing = false
  if (state.audienceToApplySelection) {
    const toApplyAudienceSelection = state.audienceToApplySelection
    state.audienceToApplySelection = null
    // @recursivity
    setAudiences(toApplyAudienceSelection)
  } else if (!hasError) {
    refreshInventory()
  }
}
